import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Plus, X } from "lucide-react";

// Default first-login password for newly empaneled doctors.
// Read from env (REACT_APP_DEFAULT_DOCTOR_PASSWORD) so it is not hard-coded
// in the bundle for production builds; falls back to the dev placeholder.
const DEFAULT_DOCTOR_PASSWORD = process.env.REACT_APP_DEFAULT_DOCTOR_PASSWORD || "Doctor@2026";

const emptyForm = {
  name: "", email: "", password: DEFAULT_DOCTOR_PASSWORD,
  specialty: "Pulmonologist & Sleep Medicine", qualifications: "", nmc_number: "",
  city: "New Delhi", hospital: "", address: "",
  experience_years: 10, languages: "English, Hindi",
  fee_in_person: 1500, fee_telemedicine: 1200,
  telemedicine: true, accepting_new: true,
  bio: "", focus_areas: ""
};

export default function Admin() {
  const { user, ready } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("bookings");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  const load = () => {
    api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/admin/bookings").then(r => setBookings(r.data.bookings)).catch(() => {});
  };

  useEffect(() => { if (ready && user?.role === "admin") load(); }, [ready, user]);

  if (ready && (!user || user.role !== "admin")) return <Navigate to="/login" replace />;
  if (!ready || !user) return <div className="sv-dash"><div className="spinner" /></div>;

  const submit = async () => {
    setCreating(true); setErr(""); setSuccess("");
    try {
      await api.post("/admin/doctors", {
        ...form,
        languages: form.languages.split(",").map(s => s.trim()).filter(Boolean),
        focus_areas: form.focus_areas.split(",").map(s => s.trim()).filter(Boolean),
        experience_years: parseInt(form.experience_years) || 0,
        fee_in_person: parseInt(form.fee_in_person) || 0,
        fee_telemedicine: parseInt(form.fee_telemedicine) || 0,
      });
      setSuccess(`Doctor ${form.name} created. Login: ${form.email} / ${form.password}`);
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="admin-page">
      <Nav theme="light" />
      <div className="sv-dash">
        <div className="container-x">
          <span className="eyebrow eyebrow-navy">Somveil Admin</span>
          <h1>Mission control.</h1>
          <p className="sv-dash-sub">Empanel doctors, monitor bookings, view mocked email log.</p>

          {stats && (
            <div className="dash-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 32 }}>
              <div className="sv-score-card"><div className="n">{stats.total_quiz_submissions}</div><div className="l">Quiz submissions</div></div>
              <div className="sv-score-card"><div className="n">{stats.total_bookings}</div><div className="l">Total bookings</div></div>
              <div className="sv-score-card"><div className="n">{stats.total_doctors}</div><div className="l">Empaneled doctors</div></div>
              <div className="sv-score-card"><div className="n" style={{ color: "#DC2626" }}>{stats.high_risk_count}</div><div className="l">High-risk results</div></div>
            </div>
          )}

          <div style={{ display: "flex", gap: 12, marginBottom: 24, borderBottom: "1px solid var(--line)" }}>
            {["bookings", "doctors"].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{ background: "transparent", border: "none", padding: "12px 4px", borderBottom: tab === t ? "2px solid var(--navy)" : "2px solid transparent", fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer", color: tab === t ? "var(--navy)" : "var(--ink-60)" }} data-testid={`admin-tab-${t}`}>
                {t}
              </button>
            ))}
          </div>

          {success && <div style={{ padding: 14, background: "rgba(78,205,196,0.12)", color: "var(--teal)", borderRadius: 10, marginBottom: 16, fontSize: 14 }} data-testid="admin-success">{success}</div>}

          {tab === "bookings" && (
            <div data-testid="admin-bookings-list">
              {bookings.length === 0 && <div style={{ padding: 60, textAlign: "center", background: "white", borderRadius: "var(--radius-lg)", border: "1px solid var(--line)", color: "var(--ink-60)" }}>No bookings yet.</div>}
              {bookings.map(b => (
                <div key={b.id} className="sv-booking-row">
                  <div><div className="k">Patient</div><div className="name">{b.patient_name}</div><div className="v">{b.patient_city}</div></div>
                  <div><div className="k">Doctor</div><div className="v">{b.doctor_name}</div></div>
                  <div><div className="k">Test</div><div className="v">Home study · {b.preferred_date}</div></div>
                  <div><div className="k">Risk</div><div className="v">{b.quiz_result?.riskLabel || "Not taken"}</div></div>
                  <div><span className={`sv-badge-pill ${b.status}`}>{b.status}</span></div>
                </div>
              ))}
            </div>
          )}

          {tab === "doctors" && (
            <>
              <div style={{ marginBottom: 20 }}>
                <button className="btn btn-dark" onClick={() => setShowForm(!showForm)} data-testid="toggle-doctor-form">
                  {showForm ? <><X size={14} /> Cancel</> : <><Plus size={14} /> Add new doctor</>}
                </button>
              </div>
              {showForm && (
                <div style={{ background: "white", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: 28, marginBottom: 20 }} data-testid="doctor-form">
                  <h3 style={{ fontFamily: "var(--font-display)", fontSize: 22, margin: "0 0 16px", fontWeight: 500 }}>Empanel a doctor</h3>
                  <div className="form-2col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {[
                      ["name", "Full name (e.g. Dr. Rakesh Kumar)"],
                      ["email", "Login email"],
                      ["password", "Temporary password"],
                      ["specialty", "Specialty"],
                      ["qualifications", "Qualifications (MD, DM, etc)"],
                      ["nmc_number", "NMC Registration Number"],
                      ["city", "City"],
                      ["hospital", "Hospital"],
                      ["address", "Full address"],
                      ["experience_years", "Experience (years)"],
                      ["languages", "Languages (comma separated)"],
                      ["fee_in_person", "In-person fee (INR)"],
                      ["fee_telemedicine", "Telemedicine fee (INR)"],
                      ["focus_areas", "Focus areas (comma separated)"],
                    ].map(([k, label]) => (
                      <div key={k}>
                        <label className="sv-label">{label}</label>
                        <input className="sv-input" value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`doctor-field-${k}`} />
                      </div>
                    ))}
                  </div>
                  <label className="sv-label" style={{ marginTop: 12 }}>Bio</label>
                  <textarea className="sv-input" rows={3} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} data-testid="doctor-field-bio" />
                  {err && <p className="sv-err" style={{ marginTop: 12 }}>{err}</p>}
                  <button onClick={submit} disabled={creating} className="btn btn-dark" style={{ marginTop: 20 }} data-testid="submit-doctor-btn">
                    {creating ? "Creating..." : "Empanel doctor"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
